/**
 * Canonical lifecycle names. These four strings are the contract between data
 * (GLOBALDEF_SUBSTATUSES.lifeCycle) and the dashboard's drag rules. Any column
 * whose lifecycle name is NOT one of these renders read-only.
 */
export const LIFECYCLES = {
  ORDER_RECEIVED: 'Order Received',
  AT_DISPATCHING: 'At Dispatching Location',
  EN_ROUTE: 'En Route',
  COMPLETE: 'Complete',
} as const

export type ContractLifecycle = typeof LIFECYCLES[keyof typeof LIFECYCLES]

export type DragAction =
  | 'reset'
  | 'assignVehicle'
  | 'markStarted'
  | 'pickCompleteOrCancel'

export interface DragRule {
  action: DragAction
  /** Whether this drop target is reachable when the order is in a driver-controlled state in Today mode. */
  requiresOverrideWarning?: boolean
}

/**
 * Maps a lifecycle name (drop target) to the action the dispatcher's drop should fire.
 * The set of keys here is the contract.
 */
export const DRAG_RULES: Record<ContractLifecycle, DragRule> = {
  [LIFECYCLES.ORDER_RECEIVED]: { action: 'reset' },
  [LIFECYCLES.AT_DISPATCHING]: { action: 'assignVehicle' },
  [LIFECYCLES.EN_ROUTE]: { action: 'markStarted' },
  [LIFECYCLES.COMPLETE]: { action: 'pickCompleteOrCancel' },
}

/** Driver-controlled lifecycles in Today mode. Drag OUT of these triggers the override warning. */
export const DRIVER_CONTROLLED_LIFECYCLES: ContractLifecycle[] = [
  LIFECYCLES.EN_ROUTE,
]

/**
 * Lifecycles from which the dispatcher's "Reassign vehicle" action is available.
 * Both open the assign-vehicle flow (transitionOrder → 'At Dispatching Location'):
 * from Order Received it's the first assignment; from At Dispatching it re-assigns.
 */
export const REASSIGN_VEHICLE_LIFECYCLES: ContractLifecycle[] = [
  LIFECYCLES.ORDER_RECEIVED,
  LIFECYCLES.AT_DISPATCHING,
]

/** Whether "Reassign vehicle" should be enabled for an order in the given lifecycle. */
export function canReassignVehicleFromLifecycle(name: string): boolean {
  return REASSIGN_VEHICLE_LIFECYCLES.includes(name as ContractLifecycle)
}

/**
 * Lifecycles whose cards the dispatcher can no longer pick up. Once a driver is
 * En Route the trip is under way, and a Complete stop is history — neither may be
 * re-sequenced or re-assigned by dragging. Drops *into* these columns stay legal
 * (that's how a stop is marked started/complete); only dragging the cards out of
 * them, or re-ordering them, is off-limits.
 */
export const DRAG_LOCKED_LIFECYCLES: ContractLifecycle[] = [
  LIFECYCLES.EN_ROUTE,
  LIFECYCLES.COMPLETE,
]

/** Whether a card in the given lifecycle is locked against being dragged. */
export function isDragLockedLifecycle(name: string): boolean {
  return DRAG_LOCKED_LIFECYCLES.includes(name as ContractLifecycle)
}

/** Minimal shape the drop rules need off a board card. */
interface DropCandidate {
  id: string
  OrderStatus?: string
}

/**
 * A stop the driver is already En Route to, or has Completed, can't be leapfrogged
 * — dropping a card above one would schedule work ahead of a trip already under way
 * or already done. Returns the first such stop at or after the drop slot (the
 * blocker), or null when the drop is allowed.
 *
 * `dropIndex` is @hello-pangea/dnd's destination index, i.e. an index into the
 * destination list with the dragged card removed — so the dragged card is filtered
 * out first, exactly like `priorStopAt`.
 */
export function committedStopBlockerAt<T extends DropCandidate>(
  destOrders: T[],
  dropIndex: number,
  draggedId: string,
  substatusMap: Record<string, string>,
): T | null {
  const list = destOrders.filter((o) => o.id !== draggedId)
  return list.slice(dropIndex).find((o) => isDragLockedLifecycle(substatusMap[o.OrderStatus || ''])) || null
}

export const CONTRACT_LIFECYCLES: ContractLifecycle[] =
  Object.values(LIFECYCLES) as ContractLifecycle[]

export function isContractLifecycle(name: string): name is ContractLifecycle {
  return CONTRACT_LIFECYCLES.includes(name as ContractLifecycle)
}
